import * as THREE from 'three';
import type { Character } from '@/schemas';
import { DEFAULT_GAME_CONFIG } from '@/schemas';
import { SoundManager } from '@/utils/SoundManager';
import type { AnimatedModelData } from '@/utils/AnimatedModelLoader';
import { AnimationStateManager } from './AnimationStateManager';

export class Player {
  public model: THREE.Object3D;
  private character: Character;
  private velocity: THREE.Vector3;
  private isJumping: boolean;
  private isGrounded: boolean;
  private readonly jumpPower: number;
  private readonly gravity: number;
  private readonly groundY: number = 0;
  private soundManager: SoundManager | null = null;
  private animationManager: AnimationStateManager;
  private boxHelper: THREE.Box3Helper | null = null; // デバッグ用
  private cachedBoundingBox: THREE.Box3 | null = null; // キャッシュされたバウンディングボックス
  private lastPosition: THREE.Vector3; // 前回の位置（キャッシュ更新判定用）

  // デバッグフラグ：trueにするとバウンディングボックスが表示される
  private static readonly DEBUG_MODE = false;

  constructor(character: Character, modelData: AnimatedModelData, soundManager?: SoundManager) {
    this.character = character;
    this.model = modelData.model;
    this.velocity = new THREE.Vector3(0, 0, 0);
    this.isJumping = false;
    this.isGrounded = true;
    this.jumpPower = DEFAULT_GAME_CONFIG.jumpPower;
    this.gravity = DEFAULT_GAME_CONFIG.gravity;
    this.soundManager = soundManager || null;

    // AnimationStateManagerを初期化
    this.animationManager = new AnimationStateManager(this.model, modelData.animations);

    // プレイヤーの初期位置（画面左側）
    // AnimatedModelLoaderで既に底面をY=0に調整済みなので、position.y=0で地面に配置
    this.model.position.set(-3, 0, 0);
    
    // lastPositionをmodel.positionと同期（初期化の整合性を保つ）
    this.lastPosition = this.model.position.clone();

    // デバッグ用：バウンディングボックスを表示
    if (Player.DEBUG_MODE) {
      const box = new THREE.Box3().setFromObject(this.model);
      this.boxHelper = new THREE.Box3Helper(box, 0x00ff00); // 緑色、半透明
      // Material | Material[] の型チェック
      const material = this.boxHelper.material;
      if (!Array.isArray(material)) {
        material.transparent = true;
        material.opacity = 0.3;
      }
    }
  }

  /**
   * ジャンプを実行
   */
  public jump(): void {
    if (this.isGrounded && !this.isJumping) {
      this.velocity.y = this.jumpPower;
      this.isJumping = true;
      this.isGrounded = false;

      // ジャンプアニメーションに切り替え
      this.animationManager.setState('jumping', false);

      // ジャンプ音を再生
      if (this.soundManager) {
        this.soundManager.playJump();
      }
    }
  }

  /**
   * プレイヤーの状態を更新
   */
  public update(deltaTime: number): void {
    // 重力を適用
    if (!this.isGrounded) {
      this.velocity.y -= this.gravity * deltaTime;
    }

    // 位置を更新
    const previousY = this.model.position.y;
    this.model.position.y += this.velocity.y * deltaTime;
    const yOffset = this.model.position.y - previousY;

    // バウンディングボックスの更新
    // Y座標のみが変わるので、キャッシュがあればY方向のオフセットだけ調整（高速）
    if (this.cachedBoundingBox && Math.abs(yOffset) > 0.001) {
      // キャッシュがある場合はY方向のオフセットだけ調整（高速）
      this.cachedBoundingBox.min.y += yOffset;
      this.cachedBoundingBox.max.y += yOffset;
      this.lastPosition.copy(this.model.position);
    } else if (!this.cachedBoundingBox || Math.abs(this.model.position.y - this.lastPosition.y) > 0.001) {
      // キャッシュがない、または位置が大きく変わった場合は再計算
      this.cachedBoundingBox = new THREE.Box3().setFromObject(this.model);
      this.lastPosition.copy(this.model.position);
    }

    // 地面との衝突判定
    // モデルの底面がgroundY以下になったら着地
    // キャッシュが確実に存在することを保証（nullチェック）
    if (!this.cachedBoundingBox) {
      this.cachedBoundingBox = new THREE.Box3().setFromObject(this.model);
      this.lastPosition.copy(this.model.position);
    }
    const modelBottom = this.cachedBoundingBox.min.y;

    if (modelBottom <= this.groundY) {
      // 底面を地面に合わせる
      const adjustment = this.groundY - modelBottom;
      this.model.position.y += adjustment;
      
      // 位置が調整されたので、バウンディングボックスのキャッシュも更新
      if (this.cachedBoundingBox) {
        this.cachedBoundingBox.min.y += adjustment;
        this.cachedBoundingBox.max.y += adjustment;
        this.lastPosition.copy(this.model.position);
      }
      
      this.velocity.y = 0;
      this.isGrounded = true;
      this.isJumping = false;

      // 着地したら走るアニメーションに戻す
      if (this.animationManager.getCurrentState() === 'jumping') {
        this.animationManager.setState('running', false);
      }
    }

    // アニメーションを更新
    this.animationManager.update(deltaTime);

    // デバッグ用：バウンディングボックスを更新
    if (Player.DEBUG_MODE && this.boxHelper && this.cachedBoundingBox) {
      // キャッシュされたバウンディングボックスを使用
      const size = this.cachedBoundingBox.getSize(new THREE.Vector3());
      const center = this.cachedBoundingBox.getCenter(new THREE.Vector3());

      this.boxHelper.position.copy(center);
      this.boxHelper.scale.set(size.x, size.y, size.z);
    }
  }

  /**
   * ゲーム開始時に走るアニメーションを開始
   */
  public startRunning(): void {
    this.animationManager.setState('running', false);
  }

  /**
   * バウンディングボックスを取得（衝突判定用）
   */
  public getBoundingBox(): THREE.Box3 {
    // キャッシュがあればそれを使用、なければ計算してキャッシュ
    if (!this.cachedBoundingBox) {
      this.cachedBoundingBox = new THREE.Box3().setFromObject(this.model);
      this.lastPosition.copy(this.model.position);
    } else {
      // 位置が変わっている場合は再計算（update()で更新されていない場合の保険）
      const yDiff = Math.abs(this.model.position.y - this.lastPosition.y);
      if (yDiff > 0.001) {
        this.cachedBoundingBox.setFromObject(this.model);
        this.lastPosition.copy(this.model.position);
      }
    }
    // intersectsBox()は元のBox3を変更しないので、cloneは不要
    return this.cachedBoundingBox;
  }

  /**
   * バウンディングボックスヘルパーを取得（デバッグ用）
   */
  public getBoxHelper(): THREE.Box3Helper | null {
    return this.boxHelper;
  }

  /**
   * プレイヤーの位置を取得
   */
  public getPosition(): THREE.Vector3 {
    return this.model.position.clone();
  }

  /**
   * キャラクター情報を取得
   */
  public getCharacter(): Character {
    return this.character;
  }

  /**
   * 地面に着地しているかどうかを取得
   */
  public getIsGrounded(): boolean {
    return this.isGrounded;
  }

  /**
   * リセット（ゲーム再開時用）
   */
  public reset(): void {
    // 初期位置に戻す
    this.model.position.set(-3, 0, 0);
    this.velocity.set(0, 0, 0);
    this.isJumping = false;
    this.isGrounded = true;
    this.lastPosition.set(-3, 0, 0);
    this.cachedBoundingBox = null; // キャッシュをクリア

    // 走るアニメーションに戻す
    this.animationManager.setState('running', true);
  }

  /**
   * クリーンアップ
   */
  public dispose(): void {
    this.animationManager.dispose();
  }
}
